import type { AuditActivityType } from "@/packages/types";
import type { ActivityEntityConfig, ChangesMap, RawActivity } from "./types";
import { renderAuditStatusChange } from "./renderers/AuditStatusChangeRenderer";
import { renderAuditCreated } from "./renderers/AuditCreatedRenderer";

const EVENT_TYPE_CATEGORIES: Partial<
  Record<AuditActivityType, "status" | "content" | "people" | "system">
> = {
  created: "status",
  status: "status",
  lifecycle: "status",
  update: "content",
  summary: "system",
  checklist: "system",
  failure: "system",
};

// Helper function to format status with title case
const formatStatus = (status: string): string => {
  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
};

const EVENT_TYPE_LABELS: Partial<
  Record<
    AuditActivityType,
    string | ((raw: RawActivity<AuditActivityType>) => string)
  >
> = {
  created: "Created this audit",
  status: (raw) => {
    const previousStatus =
      raw.metadata?.previousStatus ??
      raw.metadata?.from ??
      raw.metadata?.oldStatus;
    const newStatus =
      raw.metadata?.newStatus ?? raw.metadata?.to ?? raw.metadata?.status;

    if (previousStatus && newStatus && previousStatus !== newStatus) {
      return `Status changed: ${formatStatus(String(previousStatus))} → ${formatStatus(String(newStatus))}`;
    }
    return "Changed the status";
  },
  lifecycle: "Updated audit lifecycle",
  update: "Updated the audit",
  summary: "Generated AI summary",
  checklist: "Generated checklist",
  failure: (raw) => {
    const reason = raw.metadata?.reason ?? "Unknown error";
    return `Generation failed: ${reason}`;
  },
};

const FIELD_TITLE_LABELS: Record<string, string> = {
  displayName: "Updated the name",
  description: "Updated the description",
  deadline: "Changed the deadline",
  notes: "Updated notes",
  trigger: "Updated the trigger",
  isPublished: "Changed published status",
  tags: "Updated tags",
};

export const AUDIT_FIELD_LABELS: Record<string, string> = {
  displayName: "Name",
  description: "Description",
  deadline: "Deadline",
  notes: "Notes",
  trigger: "Trigger",
  isPublished: "Published",
  tags: "Tags",
};

export const auditActivityConfig: ActivityEntityConfig<AuditActivityType> = {
  eventTypeCategories: EVENT_TYPE_CATEGORIES,
  defaultCategory: "system",
  eventTypeLabels: EVENT_TYPE_LABELS,
  fieldLabels: AUDIT_FIELD_LABELS,
  fieldTitleLabels: FIELD_TITLE_LABELS,
  metadataSkipKeys: new Set([
    "changes",
    "success",
    "data",
    "error",
    "requestId",
    "fileName",
    "fileType",
    "previousStatus",
    "newStatus",
    "from",
    "to",
    "oldStatus",
    "status",
    "reason",
    "errorCode",
  ]),
  systemActorName: "Audit AI",

  eventTypeRenderers: {
    created: {
      renderContent: renderAuditCreated,
    },
    status: {
      renderContent: renderAuditStatusChange,
    },
  },

  getTitle(
    raw: RawActivity<AuditActivityType>,
    changes: ChangesMap | undefined,
  ): string {
    if (changes && raw.type === "update") {
      const fields = Object.keys(changes);
      if (fields.length === 1) {
        return FIELD_TITLE_LABELS[fields[0]] ?? `Updated ${fields[0]}`;
      }
      if (fields.length > 1) {
        return `Updated ${fields.length} fields`;
      }
    }
    // Lifecycle events carry human-readable descriptions from the backend
    if (
      raw.type === "lifecycle" &&
      raw.description &&
      !/[._]/.test(raw.description)
    ) {
      return raw.description;
    }
    const label = EVENT_TYPE_LABELS[raw.type];
    if (typeof label === "function") {
      return label(raw);
    }
    return label ?? raw.description;
  },
};
